import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { AppConfig } from '../config';
import { LoaderService } from './loader.service';
import { ToastService } from './toast.service';
import { CryptoService } from './crypto.service';
import { ApiResponse, ApiRequestOptions } from '../models/api.models';

const DEFAULT_OPTIONS: ApiRequestOptions = {
  showToaster: true,
  showLoader: true,
  useToken: true,
};

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);
  private config = inject(AppConfig);
  private loaderService = inject(LoaderService);
  private toastService = inject(ToastService);
  private cryptoService = inject(CryptoService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    endpoint: string,
    body?: unknown,
    options: Partial<ApiRequestOptions> = {}
  ): Promise<ApiResponse<T>> {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const url = `${this.config.settings.apiUrl}${endpoint}`;
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    // Check if logged in directly without calling AuthService to avoid circular dependency
    const isLoggedIn = isPlatformBrowser(this.platformId) && !!localStorage.getItem('accessToken');

    if (opts.useToken && isLoggedIn) {
      try {
        const encryptedToken = localStorage.getItem('accessToken') ?? '';
        const token = this.cryptoService.doubleDecrypt(encryptedToken) as string;
        headers = headers.set('Authorization', `Bearer ${token}`);
      } catch {
        // token unavailable — proceed without auth header
      }
    }

    if (opts.showLoader) {
      // Loader is managed by loaderInterceptor via reference counting.
      // X-Skip-Loader header tells the interceptor to skip this request.
    }

    // Apply any caller-supplied extra headers (e.g. Cache-Control)
    if (opts.extraHeaders) {
      for (const [key, value] of Object.entries(opts.extraHeaders)) {
        headers = headers.set(key, value);
      }
    }

    // Tell the loader interceptor to skip this request when showLoader is false
    if (!opts.showLoader) {
      headers = headers.set('X-Skip-Loader', 'true');
    }

    try {
      const raw = await firstValueFrom(
        this.http.request<ApiResponse<T>>(method, url, {
          headers,
          body: body ? JSON.stringify(body) : undefined,
        })
      );

      // Normalize: backend may return { success, statusCode } instead of { status, code }
      const resp: ApiResponse<T> = {
        ...raw,
        status: raw.status ?? (raw['success'] as boolean) ?? false,
        code: raw.code ?? (raw['statusCode'] as number) ?? 200,
      };

      if (resp.status && opts.showToaster && resp.message) {
        this.toastService.success(resp.message);
      } else if (!resp.status && opts.showToaster && resp.message) {
        this.toastService.error(resp.message);
      }

      return resp;
    } catch (error: unknown) {
      const err = error as HttpErrorResponse & { error?: ApiResponse<T> };

      if (err.status === 401) {
        if (isPlatformBrowser(this.platformId)) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('userProfile');
          sessionStorage.removeItem('pendingVerificationEmail');
        }
        this.router.navigate(['/login']);
        return { message: 'Unauthorized', code: 401, status: false };
      }

      const errBody = err.error as Record<string, unknown> | null | undefined;

      // ── Structure 1: Custom API body { success, message, statusCode, errors }
      // errors may be null — must check explicitly before treating as ASP.NET errors object
      if (errBody && typeof errBody === 'object' && ('success' in errBody || 'message' in errBody)) {
        const body: ApiResponse<T> = {
          status: (errBody['success'] as boolean) ?? false,
          message: (errBody['message'] as string) ?? 'An error occurred.',
          code: (errBody['statusCode'] as number) ?? err.status ?? 500,
          data: (errBody['data'] as T) ?? undefined,
        };
        if (!body.status && opts.showToaster && body.message) {
          this.toastService.error(body.message);
        } else if (body.status && opts.showToaster && body.message) {
          this.toastService.success(body.message);
        }
        return body;
      }

      // ── Structure 2: ASP.NET Core ValidationProblemDetails { title, errors: { Field: [...] } }
      // errors must be a non-null object (typeof null === 'object' in JS, so explicit null check required)
      if (errBody && typeof errBody === 'object'
          && 'errors' in errBody
          && errBody['errors'] !== null
          && typeof errBody['errors'] === 'object') {
        const validationErrors = errBody['errors'] as Record<string, string[]>;
        const firstMessage = Object.values(validationErrors).flat().find(Boolean)
          ?? (errBody['title'] as string | undefined)
          ?? 'Validation failed.';
        if (opts.showToaster) this.toastService.error(firstMessage);
        return { message: firstMessage, code: err.status ?? 400, status: false };
      }

      // ── Fallback
      const message = (errBody?.['message'] as string) ?? 'An unexpected error occurred.';
      if (opts.showToaster) this.toastService.error(message);
      return { message, code: err.status ?? 500, status: false };
    } finally {
      // Loader is managed by loaderInterceptor — no manual hide needed here.
    }
  }
}
